import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CalendarModule } from 'primeng/calendar';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { TableModule } from 'primeng/table';
import { AdminService } from '../../../../services/admin.service';
import { CommonService } from '../../../../services/common.service';

interface FormRow {
  label: string;
  type: 'date-range' | 'process' | 'sub-process' | 'status-indicator' | 'status-code' | 'user-id' | 'run-report';
}

interface DropdownOption {
  label: string;
  value: any;
}

// Add interface for criteria data
interface CriteriaItem {
  label: string;
  value: string;
}

@Component({
    selector: 'app-tlr-health',
    imports: [
        CommonModule,
        FormsModule,
        CalendarModule,
        ButtonModule,
        DropdownModule,
        TableModule
    ],
    templateUrl: './tlr-health.component.html',
    styleUrl: './tlr-health.component.scss'
})
export class TlrHealthComponent implements OnInit {
  // Form rows definition for p-table
  formRows: FormRow[] = [
    { label: 'Date Range', type: 'date-range' },
    { label: 'Process Name', type: 'process' },
    { label: 'Sub Process Name', type: 'sub-process' },
    { label: 'Status Code Indicator', type: 'status-indicator' },
    { label: 'Status Code', type: 'status-code' },
    { label: 'User ID', type: 'user-id' },
    { label: '', type: 'run-report' }
  ];

  // Current date for default values
  currentDate = new Date();
  
  // Date objects for calendar
  startDateObj: Date = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), this.currentDate.getDate() - 7);
  toDateObj: Date = new Date();
  
  // Date string displays in MM/dd/yyyy format - defaults to current date
  startDate: string = this.formatDate(this.startDateObj);
  toDate: string = this.formatDate(this.toDateObj);
  
  // Initialize empty dropdown options - will be filled from API
  processOptions: DropdownOption[] = [];
  subProcessOptions: DropdownOption[] = [];
  statusCodeIndicatorOptions: DropdownOption[] = [];
  statusCodeOptions: DropdownOption[] = [];

  // Selected values - default will be set from API response
  selectedProcess: string = '';
  selectedSubProcess: string = '';
  selectedStatusCodeIndicator: string = '';
  selectedStatusCode: string = '';
  userId: string = '';
  
  // Store the TLR health data
  healthData: any;
  
  // Loading state
  isLoading: boolean = false;
  
  // Report results
  reportResults: any[] = [];
  showResults: boolean = false;

  // Criteria data for the results page
  criteriaData: CriteriaItem[] = [];
  
  // Report columns for results table
  reportColumns: any[] = [];
  facilityId: string = '';

  constructor(
    private adminService: AdminService,
    private commonService: CommonService
  ) { }

  ngOnInit(): void {
    // Get TLR Health data from admin service
    this.isLoading = true;
    this.adminService.getTLRHealth().subscribe({
      next: (response: any) => {
        this.healthData = response;
        
        // Populate dropdown options directly from the API response
        if (response) {
          // Process names
          if (response.ProcessName && response.ProcessName.length > 0) {
            this.processOptions = response.ProcessName.map((item: any) => ({
              label: item.Text,
              value: item.Value
            }));
            
            // Set default selected value to first item (which should be "All")
            if (this.processOptions.length > 0) {
              this.selectedProcess = this.processOptions[0].value;
            }
          }
          
          // Sub-process names
          if (response.SubProcessName && response.SubProcessName.length > 0) {
            this.subProcessOptions = response.SubProcessName.map((item: any) => ({
              label: item.Text,
              value: item.Value
            }));
            
            // Set default selected value to first item (which should be "All")
            if (this.subProcessOptions.length > 0) {
              this.selectedSubProcess = this.subProcessOptions[0].value;
            }
          }
          
          // Status code indicators
          if (response.StatusCodeIndicator && response.StatusCodeIndicator.length > 0) {
            this.statusCodeIndicatorOptions = response.StatusCodeIndicator.map((item: any) => ({
              label: item.Text,
              value: item.Value
            }));
            
            // Set default selected value to first item (which should be "All")
            if (this.statusCodeIndicatorOptions.length > 0) {
              this.selectedStatusCodeIndicator = this.statusCodeIndicatorOptions[0].value;
            }
          }
          
          // Status codes
          if (response.StatusCode && response.StatusCode.length > 0) {
            this.statusCodeOptions = response.StatusCode.map((item: any) => ({
              label: item.Text,
              value: item.Value
            }));
            
            // Set default selected value to first item (which should be "All")
            if (this.statusCodeOptions.length > 0) {
              this.selectedStatusCode = this.statusCodeOptions[0].value;
            }
          }
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error fetching TLR Health data:', error);
        this.isLoading = false;
      }
    });
  }

  /**
   * When a process is selected, filter the sub-processes accordingly
   */
  onProcessSelected(): void {
    
    // Store the original subprocess options during first load
    if (!this._originalSubProcessOptions && this.healthData?.SubProcessName) {
      this._originalSubProcessOptions = this.healthData.SubProcessName.map((item: any) => ({
        label: item.Text,
        value: item.Value,
        processValue: item.ProcessValue
      }));
    }
    
    // When the first process option (All) is selected
    if (this.selectedProcess === this.processOptions[0]?.value) {
      // Reset sub-process dropdown to show all options
      if (this._originalSubProcessOptions && this._originalSubProcessOptions.length > 0) {
        this.subProcessOptions = [...this._originalSubProcessOptions];
      } else if (this.healthData && this.healthData.SubProcessName) {
        this.subProcessOptions = this.healthData.SubProcessName.map((item: any) => ({
          label: item.Text,
          value: item.Value
        }));
      }
    } else {
      // Filter sub-processes based on selected process
      if (this._originalSubProcessOptions && this._originalSubProcessOptions.length > 0) {
        // Find the "All" option to include at the top
        const allOption = this._originalSubProcessOptions.find(item => 
          item.value === this._originalSubProcessOptions[0].value
        );
        
        // Filter subprocess options for the selected process
        const filteredOptions = this._originalSubProcessOptions.filter(item => 
          (item.processValue && item.processValue === this.selectedProcess) ||
          (item === allOption)
        );
        
        // Always include the "All" option as the first item
        if (allOption && !filteredOptions.includes(allOption)) {
          this.subProcessOptions = [allOption, ...filteredOptions.filter(item => item !== allOption)];
        } else {
          this.subProcessOptions = filteredOptions;
        }
        
        // If no matching options were found (except possibly the "All" option),
        // provide a default "No options available" entry
        if (this.subProcessOptions.length === 0 || 
            (this.subProcessOptions.length === 1 && this.subProcessOptions[0] === allOption)) {
          this.subProcessOptions = [
            ...(allOption ? [allOption] : []),
            { label: 'No subprocess options available', value: '' }
          ];
        }
      }
    }
    
    // Reset sub-process selection to first option
    if (this.subProcessOptions && this.subProcessOptions.length > 0) {
      this.selectedSubProcess = this.subProcessOptions[0].value;
    } else {
      // If no options are available, provide a default option
      this.subProcessOptions = [{ label: 'No options available', value: '' }];
      this.selectedSubProcess = '';
    }
    
  }
  
  // Private property to store original subprocess options for filtering
  private _originalSubProcessOptions: any[] = [];

  /**
   * Handle start date selection from calendar
   */
  onStartDateSelect(): void {
    // Format date as M/d/yyyy
    this.startDate = this.formatDate(this.startDateObj);
  }

  /**
   * Handle to date selection from calendar
   */
  onToDateSelect(): void {
    // Format date as M/d/yyyy
    this.toDate = this.formatDate(this.toDateObj);
  }

  /**
   * Format date as M/d/yyyy (remove leading zeros)
   */
  private formatDate(date: Date): string {
    if (!date) return '';
    const month = date.getMonth() + 1; // getMonth() returns 0-11
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  /**
   * Run the report with current parameters
   */
  runReport(): void {
    this.isLoading = true;
    
    // Get facility ID from localStorage
    this.facilityId = localStorage.getItem('current_facility_id') || 'ES';
    
    // Prepare the parameters object
    const parameters = {
      startDate: this.startDate,
      endDate: this.toDate,
      processName: this.selectedProcess,
      subProcessName: this.selectedSubProcess,
      statusCodeIndicator: this.selectedStatusCodeIndicator,
      statusCode: this.selectedStatusCode,
      userId: this.userId || '',
      facilityId: this.facilityId
    };
    
    
    // Populate criteria data for display
    this.criteriaData = [
      { label: 'Date Range', value: `${this.startDate} to ${this.toDate}` },
      { label: 'Process Name', value: this.getSelectedLabel(this.processOptions, this.selectedProcess) },
      { label: 'Sub Process Name', value: this.getSelectedLabel(this.subProcessOptions, this.selectedSubProcess) },
      { label: 'Status Code Indicator', value: this.getSelectedLabel(this.statusCodeIndicatorOptions, this.selectedStatusCodeIndicator) },
      { label: 'Status Code', value: this.getSelectedLabel(this.statusCodeOptions, this.selectedStatusCode) },
      { label: 'User ID', value: this.userId || 'All' }
    ];
    
    // Call the admin service to get the report data
    this.adminService.getTLRHealthReport(parameters).subscribe({
      next: (response: any) => {
        
        // Process the report data to ensure it matches our static columns
        const results = Array.isArray(response) ? response : (response?.results || []);
        
        this.reportResults = results.map((result: any) => {
          // Map API response to our expected table structure
          return {
            site: result['site ID'] ||'',
            dateTime: result['date Time']  || this.formatDateTime(result['date Time']  ||''),
            processName: result['process Name'] ,
            subProcessName: result['subprocess Name']  || '',
            statusCode: result['status Code'] != null ? result['status Code'] : '',
            userId: result['cai'] || '',
            comment: result['comment']  || ''
          };
        });
        
        this.showResults = true;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error fetching report:', error);
        this.isLoading = false;
        this.reportResults = [];
      }
    });
  }

  /**
   * Format date and time as M/d/yyyy h:mm:ss AM/PM
   */
  private formatDateTime(dateValue: any): string {
    if (!dateValue) return '';
    
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    
    let hours = date.getHours();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // Convert 0 to 12
    
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    
    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds} ${ampm}`;
  }

  /**
   * Helper method to get the selected label from dropdown options
   * Converts "---All---" to "All" for display in the criteria table
   */
  getSelectedLabel(options: DropdownOption[], value: string): string {
    if (!options || options.length === 0) return '';
    
    // For display purposes, convert "---All---" value to just "All"
    if (value === '---All---') return 'All';
    
    const option = options.find(opt => opt.value === value);
    
    // Also check if the label contains "---All---" and convert it to "All"
    if (option && (option.label === '---All---' || option.label.includes('---All---'))) {
      return 'All';
    }
    
    return option ? option.label : '';
  }

  /**
   * Navigate back to the form from results view
   */
  backToForm(): void {
    this.showResults = false;
  }

  /**
   * Export report results to Excel
   */
  exportToExcel(): void {
    if (!this.reportResults || this.reportResults.length === 0) {
      return;
    }
    
    // Format the filename with date information
    const today = new Date();
    const dateStr = `${today.getFullYear()}${(today.getMonth()+1).toString().padStart(2, '0')}${today.getDate().toString().padStart(2, '0')}`;
    const timeStr = `${today.getHours().toString().padStart(2, '0')}${today.getMinutes().toString().padStart(2, '0')}`;
    const fileName = `TLR_Health_Report_${dateStr}_${timeStr}.xlsx`;
    
    // Use CommonService to export data to Excel
    this.commonService.exportToExcel(this.reportResults, fileName);
  }
}
